import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faCircleCheck, faCircleXmark } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";

export interface LinkedinCapabilitiesListProps {
	hasLinkedinPremium: boolean;
	hasLiSn: boolean;
	hasAdminRights: boolean;
	/** Sales Navigator license is held but its session is disconnected (needs reconnect). */
	liSnDisconnected?: boolean;
}

/** Display state of a single capability row. */
type CapabilityState = "yes" | "no" | "disconnected";

const STATE_ICON: Record<CapabilityState, typeof faCircleCheck> = {
	yes: faCircleCheck,
	no: faCircleXmark,
	disconnected: faCircleXmark,
};

const STATE_CLASS: Record<CapabilityState, string> = {
	yes: "text-status-success",
	no: "text-text-disabled",
	disconnected: "text-status-error",
};

const booleanState = (value: boolean): CapabilityState => (value ? "yes" : "no");

/**
 * Read-only display of the Unipile-derived LinkedIn capabilities (Premium /
 * Sales Navigator / company-page admin). The values are detected from the
 * connected LinkedIn account and cannot be edited by the user.
 *
 * Sales Navigator is tri-state: a held license whose session is disconnected
 * shows a red mark (needs reconnect), distinct from "no license" (muted mark).
 */
export const LinkedinCapabilitiesList: FunctionComponent<LinkedinCapabilitiesListProps> = ({
	hasLinkedinPremium,
	hasLiSn,
	hasAdminRights,
	liSnDisconnected = false,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.linkedinCapabilities.${key}`);

	const salesNavigatorState: CapabilityState = !hasLiSn
		? "no"
		: liSnDisconnected
			? "disconnected"
			: "yes";

	const rows: { key: string; state: CapabilityState }[] = [
		{ key: "premium", state: booleanState(hasLinkedinPremium) },
		{ key: "salesNavigator", state: salesNavigatorState },
		{ key: "companyPageAdmin", state: booleanState(hasAdminRights) },
	];

	return (
		<ul className="flex flex-col gap-2 w-full">
			{rows.map(({ key, state }) => (
				<li key={key} className="flex flex-row items-center justify-between gap-2 text-sm">
					<span>{ts(key)}</span>
					<Icon
						aria-label={`${ts(key)}: ${state}`}
						className={STATE_CLASS[state]}
						icon={STATE_ICON[state]}
					/>
				</li>
			))}
		</ul>
	);
};
